/**
 * CopyPublicLeadSummaryDialog
 * Preview and copy a public-safe lead summary for transporter platforms.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Close,
  ContentCopy,
  Email,
  Link as LinkIcon,
  Security,
  WhatsApp,
} from "@mui/icons-material";
import {
  generateLeadPublicPostTemplate,
  buildJobBoardShareMessages,
  copyToClipboard,
} from "../services/communicationTemplateService.js";

const CopyPublicLeadSummaryDialog = ({ open, onClose, leadId, jobUrl }) => {
  const [template, setTemplate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copiedAction, setCopiedAction] = useState(null);

  const shareMessages = useMemo(() => {
    if (!jobUrl || !template) return null;
    return buildJobBoardShareMessages(template, jobUrl);
  }, [jobUrl, template]);

  const previewText =
    shareMessages?.whatsapp ||
    (jobUrl && !template
      ? `Loading summary…\n\n👉 View job & submit your bid:\n${jobUrl}`
      : template);

  const loadTemplate = useCallback(async () => {
    if (!leadId) return;

    setLoading(true);
    setError(null);
    setCopiedAction(null);

    try {
      const generated = await generateLeadPublicPostTemplate(leadId);
      setTemplate(generated || "");
    } catch (err) {
      console.error("Error generating public post summary:", err);
      setError(err.message || "Failed to generate public summary");
      setTemplate("");
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    if (open && leadId) {
      loadTemplate();
    }
  }, [open, leadId, loadTemplate]);

  const handleClose = useCallback(() => {
    if (!loading) {
      setError(null);
      setCopiedAction(null);
      onClose();
    }
  }, [loading, onClose]);

  const handleCopy = useCallback(
    async (action, text) => {
      if (!text) return;

      const success = await copyToClipboard(text);
      if (success) {
        setCopiedAction(action);
        setTimeout(() => setCopiedAction(null), 2000);
      } else {
        setError("Failed to copy to clipboard");
      }
    },
    []
  );

  const handleCopyEmail = useCallback(async () => {
    if (!shareMessages) return;

    const emailBody = `Subject: ${shareMessages.emailSubject}\n\n${shareMessages.email}`;
    await handleCopy("email", emailBody);
  }, [shareMessages, handleCopy]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Security fontSize="small" color="primary" />
          {jobUrl ? "Share job link" : "Copy public-safe summary"}
        </Box>
        <Tooltip title="Close">
          <IconButton
            onClick={handleClose}
            disabled={loading}
            sx={{ position: "absolute", right: 8, top: 8 }}
            aria-label="Close dialog"
          >
            <Close />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Customer name, phone, email, full street addresses, and internal notes
          are hidden. Only area-level route details are included for public
          posting.
          {jobUrl && " The job board link is appended at the bottom as a call to action."}
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && !template && !jobUrl ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Preview
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={12}
              maxRows={20}
              value={previewText}
              InputProps={{ readOnly: true }}
              sx={{
                "& .MuiInputBase-input": {
                  fontFamily: "monospace",
                  fontSize: "0.875rem",
                  whiteSpace: "pre-wrap",
                },
              }}
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, flexWrap: "wrap", gap: 1 }}>
        <Button onClick={handleClose} disabled={loading}>
          Close
        </Button>

        {jobUrl ? (
          <>
            <Button
              variant="outlined"
              startIcon={<LinkIcon />}
              onClick={() => handleCopy("link", jobUrl)}
              disabled={!jobUrl}
            >
              {copiedAction === "link" ? "Copied!" : "Copy link"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<WhatsApp />}
              onClick={() => handleCopy("whatsapp", shareMessages?.whatsapp)}
              disabled={loading || !shareMessages}
            >
              {copiedAction === "whatsapp" ? "Copied!" : "Copy WhatsApp message"}
            </Button>
            <Button
              variant="contained"
              startIcon={<Email />}
              onClick={handleCopyEmail}
              disabled={loading || !shareMessages}
            >
              {copiedAction === "email" ? "Copied!" : "Copy email message"}
            </Button>
          </>
        ) : (
          <Button
            variant="contained"
            startIcon={<ContentCopy />}
            onClick={() => handleCopy("summary", template)}
            disabled={loading || !template}
          >
            {copiedAction === "summary" ? "Copied!" : "Copy to clipboard"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CopyPublicLeadSummaryDialog;
